import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSharedSelector } from "../../../store/SharedProvider";
import Box from "@mui/material/Box";
import ZonePlannerSideBar from "./ZonePlannerSideBar";
import ZoneControllerMouseState, { isAddingState } from "../../../enums/robot/zone-planner/zone-controller-mouse-state";
import { selectGatesData, selectRooms, selectSelectedRoom, selectSelectedRoomLimitZone } from "../../../store/room/room-selectors";
import ZoomTransformWrapper from "./ZoomTransformWrapper";
import { ReactZoomPanPinchContentRef } from "react-zoom-pan-pinch";
import { useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ZonePlannerModeState from "../../../enums/robot/zone-planner/zone-planner-mode-state";
import {
  selectChargingStationDragData,
  selectHiddenGateIds,
  selectHiddenZoneIds,
  selectSelectedZone,
  selectCurrentTeachingAutoZonePath,
  selectZonePlannerModeState,
  selectZonePlannerMouseState,
} from "../../../store/room/zone-planner/zone-planner-selectors";
import { selectUnsavedWaypoints } from "../../../store/room/room-selectors";
import ZonePlannerMapZones from "./ZonePlannerMapZones";
import LayersList from "./LayersList";
import Base64Image from "../../misc/Base64Image";
import { useTranslation } from "react-i18next";
import { calculatePolygonArea } from "../../../utils/map/zone-helper";
import { replaceArgsInMessage } from "../../../utils/strings";
import { mapScaleToValue } from "../../../utils/map/grid-scale";
import { selectSystemSettingsData } from "../../../store/robot-configurations/robot-configurations-selectors";
import Snowing from "./Snowing";
import SelectedZoneControls from "./SelectedControls/SelectedZoneControls";
import SelectedGateControls from "./SelectedControls/SelectedGateControls";
import SelectedLandmarkControls from "./SelectedControls/SelectedLandmarkControls";
import ZonePlannerMapRoomLimitZone from "./ZonePlannerMapRoomLimitZone";
import AreaInformation from "./AreaInformation";
import ZonePlannerSnackBars from "./ZonePlannerSnackBars";
import { selectDistanceUnit } from "../../../store/app-configurations/app-configurations-selectors";
import { DistanceUnit, DistanceValueUnit } from "../../../enums/configuration/units";
import useDistanceUnitValue from "../../../hooks/useDistanceUnitValue";
import { selectCurrentTeaching } from "../../../store/room/teaching/teaching-selectors";
import TeachingWizardStep from "../../../interfaces/robot/wizards/teaching/teaching-wizard-step";
import EditMapPixels from "./EditMap/EditMapPixels";
import EditMapUndoRedoAction from "../../../interfaces/robot/zone-planner/edit-map-undo-redo-action";
import CustomAlert from "../../Feedback/CustomAlert";
import ZonePlannerMapCrop from "./ZonePlannerMapCrop";

const ZonePlannerMap: React.FC<{
  inMappingWizard?: boolean;
  initialMouseState?: ZoneControllerMouseState;
  overrideRoomId?: string;
}> = ({ inMappingWizard, initialMouseState, overrideRoomId }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const roomData = useSharedSelector((state) => selectSelectedRoom(state));
  const allRoomsData = useSharedSelector((state) => selectRooms(state));
  const zonePlannerMode = useSharedSelector((state) => selectZonePlannerModeState(state));
  const zonePlannerMouseState = useSharedSelector((state) => selectZonePlannerMouseState(state));
  const chargingStationDragData = useSharedSelector((state) => selectChargingStationDragData(state));
  const hiddenZoneIds = useSharedSelector((state) => selectHiddenZoneIds(state));
  const hiddenGateIds = useSharedSelector((state) => selectHiddenGateIds(state));
  const selectedZone = useSharedSelector((state) => selectSelectedZone(state));
  const selectedRepeatPath = useSharedSelector((state) => selectCurrentTeachingAutoZonePath(state));
  const gatesData = useSharedSelector((state) => selectGatesData(state));
  const roomLimitZone = useSharedSelector((state) => selectSelectedRoomLimitZone(state));
  const systemSettings = useSharedSelector((state) => selectSystemSettingsData(state));
  const distanceUnit = useSharedSelector((state) => selectDistanceUnit(state));
  const currentTeaching = useSharedSelector((state) => selectCurrentTeaching(state));
  const waypointSavingError = useSharedSelector((state) => selectUnsavedWaypoints(state));

  const matchesSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const transformRef = useRef<ReactZoomPanPinchContentRef | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [containerSize, setContainerSize] = useState({ width: 1, height: 1 });
  const [isLayersListVisible, setIsLayersListVisible] = useState(hiddenZoneIds.length > 0 || hiddenGateIds.length > 0 || !matchesSmDown);
  const [isGridOverlayVisible, setIsGridOverlayVisible] = useState(false);
  const [hoverZoneId, setHoverZoneId] = useState<string | null>(null);
  const [hoverGateId, setHoverGateId] = useState<string | null>(null);
  const [christmasMode, setChristmasMode] = useState(false);
  const [editedMapData, setEditedMapData] = useState<{
    mapData: Uint8ClampedArray;
    undoActions: EditMapUndoRedoAction[][];
    redoActions: EditMapUndoRedoAction[][];
  } | null>(null);

  const { multiplier, label: roomLimitZoneAreaLabel } = useDistanceUnitValue(0, DistanceValueUnit.METERS_SQUARED);

  useEffect(() => {
    if (!matchesSmDown) {
      setIsLayersListVisible(true);
    }
  }, [matchesSmDown]);

  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === "h") {
        setChristmasMode((prevState) => !prevState);
      }
    };

    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, []);

// sometimes (in the tpr wizard) this component gets called with a small container height and
// because we cannot add the ref to the dependencies I made a bit of state for the height and made the layoutEffect depend on that
// I feel like there might be a prettier way to do this such as making sure the height arrives correctly from the parent or something
// This thing here basically keeps track of the height and should it change it updates the state
// it also cancels any state updates should the state change multiple times in a row that way there is at most one update scheduled at all times

  useLayoutEffect(() => {
    const wrapper = transformRef.current?.instance.wrapperComponent;
    if (!wrapper) return;

    let frame: number | null = null;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;

      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setContainerSize(prev =>
          prev.width !== width || prev.height !== height
            ? { width, height }
            : prev
        );
      });
    });

    observer.observe(wrapper);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [isInitialized]);


  useLayoutEffect(() => {
    if (transformRef.current && roomData?.mapInformation && isInitialized) {
      const { instance } = transformRef.current;

      const containerWidth = containerSize.width;
      const containerHeight = containerSize.height;

      const svgWidth = roomData.mapInformation.width;
      const svgHeight = roomData.mapInformation.height;

      const scale = Math.min(containerWidth / svgWidth, containerHeight / svgHeight);

      instance.setTransformState(
        scale,
        (containerWidth - svgWidth * scale) / 2,
        (containerHeight - svgHeight * scale) / 2
      );
      setZoomScale(scale);
    }
  }, [roomData?.mapInformation, isInitialized, containerSize, theme]);

  const zoomChangeHandler = (scale: number) => {
    setZoomScale(scale);
  };

  const toggleLayersVisible = () => {
    setIsLayersListVisible((prevState) => !prevState);
  };

  const toggleGridOverlayVisible = () => {
    setIsGridOverlayVisible((prevState) => !prevState);
  };

  const setHoverZoneIdHandler = (zoneId: string | null) => {
    setHoverZoneId(zoneId);
  };

  const setHoverGateIdHandler = (gateId: string | null) => {
    setHoverGateId(gateId);
  };

  const updateEditedMapPixelDataHandler = (mapData: Uint8ClampedArray, action: EditMapUndoRedoAction[] | null) => {
    setEditedMapData((prevState) => {
      if (prevState == null) {
        return {
          mapData,
          undoActions: action != null ? [action] : [],
          redoActions: [],
        };
      }
      return {
        ...prevState,
        mapData,
        undoActions: action != null ? [...prevState.undoActions, action] : [...prevState.undoActions],
        redoActions: action != null ? [] : [...prevState.redoActions],
      };
    });
  };

  const updateEditedMapUndoRedoHandler = (undoAction?: boolean, redoAction?: boolean, resetMap?: boolean) => {
    setEditedMapData((prevState) => {
      if (prevState == null || resetMap) {
        return null;
      }

      const undoActions = prevState.undoActions;
      const redoActions = prevState.redoActions;

      if (undoAction) {
        const lastAction = undoActions.pop();
        if (lastAction != null) {
          redoActions.push(
            lastAction?.map((action) => ({ ...action, oldColor: action.newColor, newColor: action.oldColor })),
          );
        }
      }
      if (redoAction) {
        const lastAction = redoActions.pop();
        if (lastAction != null) {
          undoActions.push(
            lastAction?.map((action) => ({ ...action, oldColor: action.newColor, newColor: action.oldColor })),
          );
        }
      }
      return { ...prevState, undoActions, redoActions };
    });
  };

  const roomLimitZoneArea =
    roomLimitZone != null ? calculatePolygonArea(roomLimitZone.points.map((p) => p.point)) : null;

  const inTeachAndRepeatWizzard = currentTeaching.currentStep === TeachingWizardStep.EditZoneStep;

  return (
    <React.Fragment>
      {waypointSavingError.map((uWp) => (
        <CustomAlert
          severity="info"
          header={replaceArgsInMessage(t("unsavedWaypointsWithErrors"), [
            `"${allRoomsData.find(r => r.id === uWp.room)?.name ?? uWp.room}"`,
            uWp.validationErrors.map((ve) => ve.error).join(", "),
          ])}
          key={uWp.room}
        />
        ))}
      <Box sx={{ height: "100%", backgroundColor: "#ebebeb", position: "relative" }}>
        {christmasMode && <Snowing />}
        <ZonePlannerSideBar
          isTransitionRoom={roomData?.secondaryStation != null}
          layersList={{ isVisible: isLayersListVisible, toggleVisible: toggleLayersVisible }}
          gridOverlay={{ isVisible: isGridOverlayVisible, toggleVisible: toggleGridOverlayVisible }}
          inMappingWizard={inMappingWizard}
          initialMouseState={initialMouseState}
          overrideRoomId={overrideRoomId}
          onEditMapUndoRedo={updateEditedMapUndoRedoHandler}
          editedMapData={editedMapData}
        />
        {isLayersListVisible &&
          zonePlannerMode !== ZonePlannerModeState.EditMap &&
          zonePlannerMode !== ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath && (
            <LayersList
              isTransitionRoom={roomData?.secondaryStation != null}
              onHoverZoneIdChange={setHoverZoneIdHandler}
              onHoverGateIdChange={setHoverGateIdHandler}
            />
          )}
        <SelectedZoneControls />
        <SelectedLandmarkControls />
        <SelectedGateControls />

        <Stack
          sx={{
            position: "absolute",
            right: theme.spacing(10),
            top: theme.spacing(1),
            lineHeight: "1.5",
            zIndex: "1",
          }}
          alignItems="flex-end"
        >
          <AreaInformation />

          {roomLimitZoneArea != null && (
            <Typography
              variant="caption"
              color={
                systemSettings?.ecoRoomLimitAreaSqm != null &&
                roomLimitZoneArea > systemSettings.ecoRoomLimitAreaSqm
                  ? "red"
                  : ""
              }
            >
              {`${t("room-limit-zone-area")}: ${(roomLimitZoneArea * multiplier).toFixed(2)}${roomLimitZoneAreaLabel}`}
            </Typography>
          )}

          {zonePlannerMode !== ZonePlannerModeState.EditMap && (
            <React.Fragment>
              {isGridOverlayVisible && roomData?.mapInformation.resolution != null && (
                <Typography variant="caption">
                  {replaceArgsInMessage(t("zoneplanner-scale-text"), [
                    mapScaleToValue(zoomScale, roomData.mapInformation.resolution, distanceUnit).gridText,
                    distanceUnit === DistanceUnit.METRIC ? t("meter-unit") : t("label-feet"),
                  ])}
                </Typography>
              )}
              {chargingStationDragData.isSelected && roomData?.mapInformation.resolution != null && (
                <Typography variant="overline">
                  {replaceArgsInMessage(t("zoneplanner-drag-station-offset"), [
                    roomData.primaryStation.coordinateX.toFixed(2),
                    roomData.primaryStation.coordinateY.toFixed(2),
                  ])}
                </Typography>
              )}
            </React.Fragment>
          )}
        </Stack>

        <ZoomTransformWrapper
          initialScale={zoomScale}
          disablePan={
            selectedZone.selectedPointId !== null ||
            selectedRepeatPath.selectedTeachAndRepeatPathPointId !== null ||
            gatesData.selectedId !== null ||
            zonePlannerMouseState === ZoneControllerMouseState.DragZone ||
            zonePlannerMouseState === ZoneControllerMouseState.DragRoomLimitationZone ||
            zonePlannerMouseState === ZoneControllerMouseState.CopyZone ||
            zonePlannerMouseState === ZoneControllerMouseState.AddGate ||
            isAddingState(zonePlannerMouseState) ||
            chargingStationDragData.isDragging ||
            zonePlannerMouseState === ZoneControllerMouseState.CropMap ||
            zonePlannerMouseState === ZoneControllerMouseState.DrawPenTool ||
            zonePlannerMouseState === ZoneControllerMouseState.DrawLineTool ||
            zonePlannerMouseState === ZoneControllerMouseState.DrawRectangleTool
          }
          onZoomChange={zoomChangeHandler}
          ref={(node) => {
            transformRef.current = node;
            setIsInitialized(true);
          }}
        >
          <Box>
            {roomData != null && (
              <React.Fragment>
                {zonePlannerMode !== ZonePlannerModeState.EditMap && (
                  <ZonePlannerMapZones
                    roomData={roomData}
                    zoomScale={zoomScale}
                    hoverZoneId={hoverZoneId}
                    hoverGateId={hoverGateId}
                    isGridVisible={isGridOverlayVisible}
                    isChristmasMode={christmasMode}
                    isWayPointMapViewable={true}
                  />
                )}

                {zonePlannerMouseState === ZoneControllerMouseState.CropMap && (
                  <ZonePlannerMapCrop
                    roomId={overrideRoomId ?? roomData.id}
                    mapWidth={roomData.mapInformation.width}
                    mapHeight={roomData.mapInformation.height}
                    zoomScale={zoomScale}
                  />
                )}

                <ZonePlannerMapRoomLimitZone roomData={roomData} zoomScale={zoomScale} />

                {zonePlannerMode === ZonePlannerModeState.EditMap && roomData.map != null ? (
                  <EditMapPixels
                    roomData={roomData}
                    zoomScale={zoomScale}
                    mapImgUrl={roomData.map}
                    onEditMap={updateEditedMapPixelDataHandler}
                    mapData={editedMapData?.mapData ?? null}
                    inMappingWizard={inMappingWizard}
                  />
                ) : (
                  <Base64Image imageString={roomData.map} alt={roomData.name} testDataId="map-planner-main-map" />
                )}
              </React.Fragment>
            )}
          </Box>
        </ZoomTransformWrapper>
      </Box>
      {!inMappingWizard && !inTeachAndRepeatWizzard && <ZonePlannerSnackBars />}
    </React.Fragment>
  );
};

export default ZonePlannerMap;
